<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/components/ui/dialog'
import { Button } from '~/components/ui/button'
import { Textarea } from '~/components/ui/textarea'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { useToast } from '~/components/ui/toast'
import { LucideLoaderCircle } from 'lucide-vue-next'
import { useProfileLeadCancelReasonsStore } from '~/stores/profileLeadCancelReasons'

const { leadId, open } = defineProps<{
  leadId: number
  open: boolean
}>()

const emit = defineEmits<{
  close: []
  cancelled: []
}>()

const { $api } = useNuxtApp()
const { toast } = useToast()
const { t } = useI18n()

const cancelReasonsStore = useProfileLeadCancelReasonsStore()

watch(() => open, (val) => {
  if (val) cancelReasonsStore.fetch()
})

const cancelReason = ref('')
const cancelComment = ref('')
const pending = ref(false)

const onSubmit = async () => {
  pending.value = true
  try {
    await $api(`/profile/leads/${leadId}/status`, {
      method: 'PATCH',
      body: {
        status: 'closed',
        cancel_reason: cancelReason.value,
        ...(cancelComment.value ? { cancel_comment: cancelComment.value } : {}),
      },
    })
    toast({ title: t('page.b2b.leads.cancel_dialog.success'), variant: 'success' })
    cancelReason.value = ''
    cancelComment.value = ''
    emit('cancelled')
    emit('close')
  } catch (err: unknown) {
    const error = err as { data?: { message?: string } }
    toast({ title: error.data?.message ?? t('common.error'), variant: 'destructive' })
  } finally {
    pending.value = false
  }
}

const onClose = () => {
  cancelReason.value = ''
  cancelComment.value = ''
  emit('close')
}
</script>

<template>
  <Dialog :open="open" @update:open="onClose">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>{{ $t('page.b2b.leads.cancel_dialog.title') }}</DialogTitle>
      </DialogHeader>

      <form class="space-y-4" @submit.prevent="onSubmit">
        <div class="space-y-1">
          <Label>{{ $t('page.b2b.leads.cancel_dialog.reason_label') }}</Label>
          <Select v-model="cancelReason">
            <SelectTrigger>
              <SelectValue :placeholder="$t('page.b2b.leads.cancel_dialog.reason_placeholder')" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="reason in cancelReasonsStore.list" :key="reason" :value="reason">
                {{ $t(`page.b2b.leads.cancel_dialog.reason_${reason}`) }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div class="space-y-1">
          <Label>{{ $t('page.b2b.leads.cancel_dialog.comment_label') }}</Label>
          <Textarea
            v-model="cancelComment"
            :placeholder="$t('page.b2b.leads.cancel_dialog.comment_placeholder')"
            rows="3"
            maxlength="1000"
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline-gray" @click="onClose">
            {{ $t('common.cancel') }}
          </Button>
          <Button type="submit" variant="destructive" :disabled="!cancelReason || pending">
            <LucideLoaderCircle v-if="pending" class="mr-2 h-4 w-4 animate-spin" />
            {{ $t('page.b2b.leads.cancel_dialog.submit') }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
